import React, { useEffect, useState } from "react";
import logo from "../assets/images/logo/logo.png";
import { Search, UserRoundX, X } from "lucide-react";
import Setting from "../assets/svgs/setting.svg";
import MealInfoCard from "../components/Cards/MealInfoCard";
import Filter from "../components/model/Filter";
import { useDispatch, useSelector } from "react-redux";
import { getLandingDetail } from "../redux/Thunk/providerThunk";
import Pagination from "../components/Pagination";
import MealCardShimmer from "../components/Shimmer/MealCardShimmer";
import { useNavigate } from "react-router-dom";

const Discoverpage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isProviderLoading } = useSelector((state) => state.providers);
  const pagination = useSelector((state) => state.providers.pagination);
  const [details, setDetails] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [vegOnly, setVegOnly] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [filters, setFilters] = useState({
    orders: "",
    price: "",
  });

  // Fetch the data from an APi
  useEffect(() => {
    dispatch(
      getLandingDetail({
        page,
        search,
        vegOnly,
        orders: filters.orders,
        price: filters.price,
      }),
    )
      .unwrap()
      .then((res) => {
        const providers = res.data?.providers || [];
        setTotalResults(res.data?.total_records || 0);

        const formatted = providers.map((item) => {
          const images = item.service_images || [];
          const coverImg =
            images.find((img) => img.is_cover_image === 1)?.image_url ||
            images[0]?.image_url ||
            "";

          const thumbnails = images.map((img) => img.image_url);

          return {
            id: item.service_provider_id,
            providerImg: coverImg,
            thumbnails,
            serviceName: item.service_name,
            servieProvider: item.owner_name,
            servedTiffins: item.total_tiffins_served,
            orderAmount: item.lowest_price,
            isVeg: item.is_pure_veg_food,
            providerLink: item.provider_link,
            Currency: item.currency_symbol,
          };
        });

        setDetails(formatted);
      });
  }, [dispatch, page, search, vegOnly, filters]);

  const filterCount =
    (vegOnly ? 1 : 0) + (filters.orders ? 1 : 0) + (filters.price ? 1 : 0);

  return (
    <>
      <div className="min-h-screen ">
        {/* Header part */}
        <header className="">
          <div
            className="flex justify-center p-4 cursor-pointer"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} 
          >
            <img src={logo} alt="logo" className="w-auto h-10" />
          </div>
        </header>
        {/* TItle section */}
        <section>
          <div className="flex justify-center flex-col items-center mt-10 space-y-3">
            <h1 className="font-bold font-outfit text-2xl md:text-3xl lg:text-5xl text-[#191919] text-center max-w-[250px] md:max-w-full leading-9">
              Discover Trusted{" "}
              <span className="text-[#FF6943]">Tiffin Services</span> in Your
              Area
            </h1>
            <p className="mt-3 mb-2 text-sm md:text-lg text-center max-w-[350px] md:max-w-full text-[#191919] font-manrope leading-7 font-normal">
              Fresh, hygienic & homemade meals delivered from trusted providers
              in your area.
            </p>
          </div>
        </section>

        {/* Search and filter section */}
        <section className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-2">
            {/* Search Section */}
            <div className="flex items-center w-full max-w-full border border-[#ECECEC] rounded-xl px-3 py-3 bg-white relative">
              <Search className="text-gray-500 mr-2" size={18} />

              <input
                type="text"
                value={search}
                onChange={(e) => {
                  const sanitized = e.target.value
                    .replace(/^\s+/, "")
                    .replace(/<[^>]*>?/gm, "")
                    .replace(/[<>]/g, "");
                  setSearch(sanitized);
                  setPage(1);
                }}
                placeholder="Search tiffins, providers, or locations"
                className="w-full outline-none text-sm text-[#303030] font-manrope placeholder:text-[#929292] placeholder:text-xs"
              />

              {search && (
                <button
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                  className="ml-2 text-gray-400 hover:text-gray-600 transition"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Settings Icon */}
            <div className="relative">
              <button
                className="px-3 py-3 rounded-xl border border-[#ECECEC] flex justify-center items-center bg-white transition"
                onClick={() => setIsFilterOpen(true)}
              >
                <img src={Setting} className="text-gray-600 w-5 h-5" />
              </button>

              {filterCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#FF6943] text-white text-[10px] font-semibold rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center">
                  {filterCount}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Details cards section */}
        <section className="max-w-7xl mx-auto px-6 py-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-6 place-items-center">
            {/* Loading Shimmer */}
            {isProviderLoading &&
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="w-full flex justify-center">
                  <MealCardShimmer />
                </div>
              ))}

            {/* Data Cards */}
            {!isProviderLoading &&
              details.length > 0 &&
              details.map((data) => (
                <div key={data.id} className="w-full flex justify-center">
                  <MealInfoCard
                    providerImg={data.providerImg}
                    thumbnails={data.thumbnails}
                    serviceName={data.serviceName}
                    servieProvider={data.servieProvider}
                    servedTiffins={data.servedTiffins}
                    orderAmount={data.orderAmount}
                    isVeg={data.isVeg}
                    onProviderDetail={() =>
                      window.open(data.providerLink, "_blank")
                    }
                    Currency={data.Currency}
                  />
                </div>
              ))}
          </div>

          {/* No Providers Found */}
          {!isProviderLoading && details.length === 0 && (
            <div className="flex justify-center items-center py-16">
              <p className="text-gray-700 text-lg font-outfit">
                <span className="flex justify-center items-center">
                  <UserRoundX size={65} className="text-gray-300" />
                </span>
                No providers found
              </p>
            </div>
          )}

          {/* Pagination */}
          <div className="mt-20">
            {!isProviderLoading && details.length > 0 && (
              <Pagination
                current={pagination.current_page}
                total={pagination.total_pages}
                onPageChange={(p) => setPage(p)}
              />
            )}
          </div>
        </section>
        {/* Contact section */}
        <section className="max-w-7xl mx-auto px-4 py-4 md:py-16">
          <div className="rounded-3xl bg-gradient-to-r from-[#FF9464] to-[#FF8161] flex flex-col justify-center items-center text-center px-6 py-8 md:py-10">
            <h1 className="text-white text-2xl md:text-3xl lg:text-4xl font-outfit font-semibold max-w-2xl leading-snug">
              Interested in Becoming a Tiffin Provider?
            </h1>

            <p className="mt-4 text-white text-sm md:text-base font-manrope max-w-xl leading-relaxed">
              Fresh, hygienic & homemade meals delivered from trusted providers
              in your area.
            </p>

            <a
              href="/join-now"
              target="_blank"
              rel="noreferrer"
              className="mt-6"
            >
              <button className="border border-white text-white rounded-full px-6 py-2.5 text-sm md:text-base font-medium hover:bg-white hover:text-[#FF8161] transition-all duration-300">
                Join Now
              </button>
            </a>
          </div>
        </section>

        {/* Footer section */}
        <footer className="py-4 border-t border-gray-200">
          <p className="text-sm text-[#191919] text-center   max-w-7xl mx-auto px-4">
            © {new Date().getFullYear()} TiffinTap. All rights reserved.
          </p>
        </footer>
      </div>

      {/* Filter modal */}
      {isFilterOpen && (
        <Filter
          onClose={() => setIsFilterOpen(false)}
          vegOnly={vegOnly}
          filters={filters}
          setVegOnly={setVegOnly}
          setFilters={setFilters}
          totalResults={totalResults}
        />
      )}
    </>
  );
};

export default Discoverpage;
